import parser from 'fast-xml-parser';
import _ from 'lodash';
import moment from 'moment';

import { evalSSEexpression, evaluateConditions } from './evaluation.utils';
import {
  getDataPath,
  getDataType,
  getDefaultValueFromEntityDefinition,
  getFilterValue,
  getFormatType,
  getPropTranslation,
  translate,
  value2valueWithType,
} from './form-manager.utils';
import { getAutoCompleteErrorsCache, getControls } from './validationStorage.utils';
import { PANEL_CONFIGURATIONS } from '../../constants/PanelLayoutConstants';
import { AUTOCOMPLETE_DROPDOWN_SIZE, SIDEBAR_WIDTH } from '../../constants/SizeConstants';
import { SSString } from '../../constants/SSTypes';
import { WARNING_SOURCES } from '../../constants/WarningsConstants';
import { formatString, getHtmlFreeValue, isTrue } from '../../utils/commonUtils';
import { getDateFormat } from '../../utils/dateUtils';
import { isNilOrEmpty, set } from '../../utils/ramdaUtils';
import { removeCustomFiltersWithEmptyValue } from '../../utils/savedSearchUtils';
import { isMobileDevice } from '../../utils/windowDimensionsUtils';
import { FORM_KEYS, SS_CONTROL_TYPE } from '../constants/form-manager.constants';
import {
  ACTION_TYPE_SET_DEFAULT,
  BAH_CHILD_DATAMEMBER,
  BAH_CHILD_RECORD_ID,
  ENTITY_DISPLAY_LEFT_RIGHT_PADDING,
  ERROR_CONTAINER_FLEX_BASIS,
  LOOKUP_TYPES,
} from '../constants/runtime.constants';
import { getSsContext } from '../selectors/form-manager.selectors';
import { getDynamicParamCollectionSelector } from '../selectors/runtime.selectors';

export const getDataForController = ({
  entityDefinition,
  entityData,
  entityName,
  fieldName,
  index,
}) => {
  const dataPath = getDataPath(entityName, index, entityDefinition);
  return _.get(entityData, [...dataPath, fieldName]);
};

export const getEntityDataWithUpdatedValues = (formDesigner, formKey, changesArray) => {
  let entityData = formDesigner[formKey].data?.entityDataStore?.entityData;
  const { entityDefinition } = formDesigner[formKey];

  changesArray.forEach((change) => {
    const { entityName, fieldName, index = [], value } = change;
    const pathWithIndexes = getDataPath(entityName, index, entityDefinition);
    entityData = set(entityData, [...pathWithIndexes, fieldName], value);
  });

  return entityData;
};

export const getFormDesignerWithUpdatedEntityData = (formDesigner, formKey, updatedEntityData) => ({
  ...formDesigner,
  [formKey]: {
    ...formDesigner[formKey],
    data: {
      ...formDesigner[formKey].data,
      entityDataStore: {
        ...formDesigner[formKey].data.entityDataStore,
        entityData: updatedEntityData,
      },
    },
  },
});

export const getGridRowsPropertiesMapBasedOnFlag = (rows, flag, isParentless = false) =>
  rows.reduce((acc, row) => {
    const key = isParentless ? row.gridArrIndex : row.index;
    acc[key] = flag;
    return acc;
  }, {});

export const hasInstance = (obj, instance) => {
  if (obj === instance) {
    return true;
  }

  if (typeof obj === 'object' && obj !== null) {
    for (const value of Object.values(obj)) {
      if (hasInstance(value, instance)) {
        return true;
      }
    }
  }

  return false;
};

// it's supposed, that an instance always exists in data
export const getPathByInstance = (instance, data, path = []) => {
  for (const [key, value] of Object.entries(data)) {
    if (value === instance) {
      return path.join('.');
    }

    if (hasInstance(value, instance)) {
      const pushKey = Number.isInteger(+key) ? `[${key}]` : key; // handles array index
      path.push(pushKey);
      return getPathByInstance(instance, value, path);
    }
  }
};

export const createPathWithRows = (
  originalEntityData,
  currenEntityData,
  dataMember,
  pathWithRowsMap
) => {
  for (const [key, value] of Object.entries(currenEntityData)) {
    if (key === dataMember && Array.isArray(value) && value.length) {
      const path = getPathByInstance(value, originalEntityData);
      pathWithRowsMap[path] = value.length;
      break;
    }

    if (typeof value === 'object' && value !== null) {
      createPathWithRows(originalEntityData, value, dataMember, pathWithRowsMap);
    }
  }
};

export const getIndexesForDataMember = (entityData, entityType, dataMember) => {
  if (dataMember === entityType || dataMember === 'V_ENU_NONPERSIS_USER_DATA') {
    // top entity (V_SS_REGULATORY)
    return [[0]];
  }

  const pathWithRowsMap = {};
  const indexes = [];
  const dataMemberDimension = dataMember.match(/-V/g).length;
  const dataMemberAncestor = dataMember
    .split('-')
    .slice(0, 2)
    .join('-');
  const subEntityData = entityData[entityType] ?? {};
  const dataMemberAncestorValue = subEntityData[dataMemberAncestor];

  if (!dataMemberAncestorValue?.length) {
    return indexes;
  }

  if (dataMemberDimension === 1) {
    // only 1 instance (V_SS_REGULATORY-V_SS_REGULATORY_EMDR)
    pathWithRowsMap[dataMember] = dataMemberAncestorValue.length;
  } else {
    createPathWithRows(entityData, dataMemberAncestorValue, dataMember, pathWithRowsMap);
  }

  const pathWithRowsEntries = Object.entries(pathWithRowsMap);

  if (pathWithRowsEntries.length === 1 && dataMemberDimension === 1) {
    // no indexes in path (V_SS_REGULATORY-V_SS_REGULATORY_EMDR)
    const rowsCount = pathWithRowsEntries[0][1];

    for (let i = 0; i < rowsCount; i++) {
      indexes.push([i]);
    }
    return indexes;
  }

  for (const [path, rowsCount] of pathWithRowsEntries) {
    const match = path.match(/\[\d+\]/g);
    const pathIndexes = match.map((strIndex) => +strIndex.slice(1, strIndex.length - 1));

    for (let i = 0; i < rowsCount; i++) {
      indexes.push([...pathIndexes, i]);
    }
  }

  return indexes;
};

export const getDataMembersIndexesMap = (dataMembers, entityData, entityType) => {
  const dataMembersIndexesMap = {};

  dataMembers.forEach((dataMember) => {
    dataMembersIndexesMap[dataMember] = getIndexesForDataMember(entityData, entityType, dataMember);
  });

  return dataMembersIndexesMap;
};

export const getControlsWithIndexes = (controls, entityData, entityType) => {
  const dataMembers = [...new Set(controls.map((control) => control.entityName))];
  const dataMembersIndexesMap = getDataMembersIndexesMap(dataMembers, entityData, entityType);

  return controls.map((control) => [control, dataMembersIndexesMap[control.entityName]]);
};

export const getWatchValuesObjectArr = (formRules, dataMembersIndexesMap) => {
  const watchValuesObjectsArr = [];

  formRules.forEach((rule) => {
    rule.Conditions.forEach((condition) => {
      switch (condition.LeftType) {
        case 'column': {
          if (!condition.LeftValue) {
            return;
          }
          const [entityName, fieldName] = condition.LeftValue.split('.');

          Array.isArray(dataMembersIndexesMap[entityName]) &&
            dataMembersIndexesMap[entityName].forEach((index) => {
              watchValuesObjectsArr.push({
                entityName,
                fieldName,
                index,
                type: 'column',
              });
            });
          break;
        }

        case 'formula': {
          Array.isArray(dataMembersIndexesMap[rule.entityName]) &&
            dataMembersIndexesMap[rule.entityName].forEach((index) => {
              watchValuesObjectsArr.push({
                value: condition.LeftValue,
                index,
                contextDataMember: rule.entityName,
                type: 'formula',
              });
            });
          break;
        }

        default:
          break;
      }

      switch (condition.RightType) {
        case 'column': {
          if (!condition.RightValue) {
            return;
          }
          const [entityName, fieldName] = condition.RightValue.split('.');

          Array.isArray(dataMembersIndexesMap[entityName]) &&
            dataMembersIndexesMap[entityName].forEach((index) => {
              watchValuesObjectsArr.push({
                entityName,
                fieldName,
                index,
                type: 'column',
              });
            });
          break;
        }

        case 'formula': {
          Array.isArray(dataMembersIndexesMap[rule.entityName]) &&
            dataMembersIndexesMap[rule.entityName].forEach((index) => {
              watchValuesObjectsArr.push({
                value: condition.RightValue,
                index,
                contextDataMember: rule.entityName,
                type: 'formula',
              });
            });
          break;
        }

        default:
          break;
      }
    });
  });

  return watchValuesObjectsArr;
};

export const xml2json = (xml) => {
  const options = {
    attributeNamePrefix: '',
    ignoreAttributes: false,
    ignoreNameSpace: false,
    allowBooleanAttributes: true,
    parseNodeValue: true,
    parseAttributeValue: false,
    trimValues: true,
    cdataTagName: '__cdata', // default is 'false'
    cdataPositionChar: '\\c',
    localeRange: '', // To support non english character in tag/attribute values.
    parseTrueNumberOnly: false,
  };

  const tempXMLObj = parser.getTraversalObj(xml, options);
  const jsonObj = parser.convertToJson(tempXMLObj, options);

  return jsonObj;
};

export const parseListString = (xmlStr, listPrefix) => {
  const parsedListObj = xml2json(xmlStr);
  const listItems = parsedListObj?.[`${listPrefix}List`]?.[`${listPrefix}ListItem`] ?? [];

  return Array.isArray(listItems) ? listItems : [listItems];
};

const createGetTextBoxSibling = (controlType) => (formModel, textBoxId) => {
  const textBox = formModel[textBoxId];
  const advancedTextBox = formModel[textBox.parent];

  return formModel[
    advancedTextBox?.childrenNodes?.find((child) => formModel[child]?.controlType === controlType)
  ];
};

const createGetVisibleTextBoxSibling = (controlType) => (formModel, textBoxId, ssContext) => {
  const textBox = formModel[textBoxId];
  const advancedTextBox = formModel[textBox.parent];

  const ssZooms = advancedTextBox?.childrenNodes?.filter(
    (child) => formModel[child]?.controlType === controlType
  );

  const hasRegulatoryZoom = advancedTextBox?.childrenNodes?.filter(
    (child) => formModel[child]?.controlType === SS_CONTROL_TYPE.REGULATORY_TERMS_ZOOM
  );

  const visibleZoom = ssZooms.find((zoom) =>
    formModel[zoom]?.hidden?.Conditions
      ? !evaluateConditions({ conditions: formModel[zoom]?.hidden?.Conditions, ssContext })
      : !isTrue(formModel[zoom]?.hidden)
  );

  // this logic is to support a work around solution to make autocomplete work on regulatory terms zooms
  if (ssZooms.length && hasRegulatoryZoom.length && !visibleZoom) {
    return formModel[ssZooms[0]];
  }

  return formModel[visibleZoom];
};

export const getVisibleZoomByTextBoxId = createGetVisibleTextBoxSibling(SS_CONTROL_TYPE.ZOOM);

export const getTextViewByTextBoxId = createGetTextBoxSibling(SS_CONTROL_TYPE.TEXT_VIEW);

export const getAutoCompleteRequestParams = ({
  value,
  instanceId,
  entityName,
  textBoxFieldName,
  textViewFieldName,
  formKey,
  reduxState,
}) => {
  const useContainsOperator = isTrue(reduxState.appSettings['TypeAhead/UseContainsOperator']);
  const { formModel } = reduxState.formDesigner[formKey];
  const id = instanceId.split('#')[0];
  const index = JSON.parse(instanceId.split('#')[1]);
  const ssContext = getSsContext(reduxState, { index, formKey, entityName });
  const ssZoom = getVisibleZoomByTextBoxId(formModel, id, ssContext);

  if (!ssZoom) {
    return {};
  }

  const { dataMappingList } = ssZoom;
  const dynamicParamCollection = getDynamicParamCollectionSelector(reduxState, {
    index,
    formKey,
    entityName,
    id: ssZoom.controlId,
  });

  const textBoxDataMapping = dataMappingList.find(
    (dm) => dm.updateTo === `${entityName}.${textBoxFieldName}`
  );
  const textViewDataMapping = dataMappingList.find(
    (dm) => dm.updateTo === `${entityName}.${textViewFieldName}`
  );
  const conditions = ssZoom.filter?.Conditions;

  const filter = conditions?.length ? transformFiltersForAPI(conditions, ssContext) : [];

  if (useContainsOperator && textViewDataMapping) {
    filter.push({
      openGroup: true,
      fieldName: textBoxDataMapping.updateFrom,
      sqlOperator: 'CT',
      value,
      closeGroup: false,
      relationalOperator: 'OR',
    });
    filter.push({
      openGroup: false,
      fieldName: textViewDataMapping.updateFrom,
      sqlOperator: 'CT',
      value,
      closeGroup: true,
      relationalOperator: 'AND',
    });
  } else {
    filter.push({
      openGroup: false,
      fieldName: textBoxDataMapping.updateFrom,
      sqlOperator: useContainsOperator ? 'CT' : 'SW',
      value,
      closeGroup: false,
      relationalOperator: 'AND',
    });
  }

  const referencedColumns = dataMappingList.reduce(
    (acc, map) => ({ ...acc, [map.updateTo]: map.updateFrom }),
    {}
  );

  const requestParams = {
    currentPageIndex: 0,
    dynamicParamCollection,
    entityName: ssZoom.dataObject,
    filter: removeCustomFiltersWithEmptyValue(filter),
    pageSize: AUTOCOMPLETE_DROPDOWN_SIZE,
    referencedColumns,
    savedSearchName: ssZoom.searchName,
    sortExpression: '',
  };

  return requestParams;
};

export const validateSsNumericTextBox = ({
  dataType,
  value,
  label,
  instanceId,
  warnings,
  translations,
}) => {
  const isNotNumber = !isNilOrEmpty(value) && Number.isNaN(+value);
  const isNotInt32 = !isNilOrEmpty(value) && +value !== +value >> 0;
  let message;

  switch (dataType) {
    case 'System.Int32':
      if (isNotNumber) {
        message = `${translate(translations, 'Please enter number for')} ${label}`;
      } else if (isNotInt32) {
        message = `${translate(translations, 'Integer value out of range for')} ${label}`;
      }
      break;

    case 'System.Decimal':
      if (isNotNumber) {
        message = `${translate(translations, 'Please enter number for')} ${label}`;
      }
      break;

    default:
      break;
  }

  if (message) {
    warnings.push({
      instanceId,
      source: WARNING_SOURCES.NUMERIC_TEXT_BOX_VALIDATION,
      message,
      warningEnabled: true,
    });
  }
};

export const validateSsDateBox = ({ value, label, instanceId, warnings, translations }) => {
  const dateFormat = getDateFormat(value);
  const date = moment(value, dateFormat);

  // value could be a string e.g. "2023-01-27T08:27:23.957", "10-may-____"
  const invalidStringDate =
    typeof value === 'string' && (value.includes('_') || !moment(value).isValid());
  // value could be a Date class instance
  const invalidDateInstance = typeof value !== 'string' && !date.isValid();

  const valueIsInvalid = value && (invalidStringDate || invalidDateInstance);

  if (valueIsInvalid) {
    const message = `${translate(translations, 'Please enter a valid date for')} ${label}`;

    warnings.push({
      instanceId,
      source: WARNING_SOURCES.DATE_BOX_VALIDATION,
      message,
      warningEnabled: true,
    });
  }
};

export const validateSsTextBox = ({ rowId, instanceId, value, label, translations, warnings }) => {
  const cache = getAutoCompleteErrorsCache();
  const id = instanceId.split('#')[0];

  if (cache[rowId]?.[id] === value) {
    warnings.push({
      instanceId,
      source: WARNING_SOURCES.AUTOCOMPLETE,
      message: `${label} ${translate(translations, 'is invalid')}.`,
      warningEnabled: true,
    });
  }
};

const validateSsSlider = ({
  value,
  label,
  instanceId,
  minValue,
  maxValue,
  warnings,
  translations,
  ssContext,
}) => {
  let valueValidationMessage;

  let evalMaxValue = maxValue;
  let evalMinValue = minValue;

  const processValue = (value) =>
    value !== undefined && !_.isBoolean(value) && value.split('eval||')[1];

  evalMaxValue = processValue(maxValue)
    ? evalSSEexpression(processValue(maxValue), ssContext)
    : maxValue;
  evalMinValue = processValue(minValue)
    ? evalSSEexpression(processValue(minValue), ssContext)
    : minValue;

  if (Number(value) > Number(evalMaxValue)) {
    valueValidationMessage = `${translate(translations, 'Value for')} ${label} ${translate(
      translations,
      'cannot be greater than the maximum'
    )} - ${Number(evalMaxValue)}`;
  } else if (Number(value) < Number(evalMinValue)) {
    valueValidationMessage = `${translate(translations, 'Value for')} ${label} ${translate(
      translations,
      'cannot be less than the minimum'
    )} - ${Number(evalMinValue)}`;
  }

  if (valueValidationMessage) {
    warnings.push({
      instanceId,
      source: WARNING_SOURCES.NUMERIC_TEXT_BOX_VALIDATION,
      message: valueValidationMessage,
      warningEnabled: true,
    });
  }
};

export const setContainersInfoForWarning = (warning) => {
  const controls = getControls();
  const { containersPropsJson } = controls[warning.instanceId] ?? {};

  if (!containersPropsJson) {
    // the control is visible
    return;
  }

  const containersProps = JSON.parse(containersPropsJson);
  const inactiveContainers = containersProps.filter(({ isActive }) => !isActive);

  if (!inactiveContainers.length) {
    // the control is visible
    return;
  }

  warning.containersInfo = inactiveContainers;
};

export const isControlInsideComplexGridColumn = (id, formModel) => {
  const parentId = formModel[id].parent;
  const parentControl = formModel[parentId];

  if (parentControl.controlType !== SS_CONTROL_TYPE.GRID_COLUMN) {
    return false;
  }

  const gridControl = formModel[parentControl.parent];
  const isGridComplex = gridControl.childrenNodes.some(
    (childId) => formModel[childId].controlType === SS_CONTROL_TYPE.SUB_GRID
  );

  return isGridComplex;
};

/**
 * skip validation if the control is hidden or it's inside a complex grid column
 */
const shouldSkipValidation = (isHidden, id, formModel) => {
  if (isHidden) {
    return true;
  }

  return isControlInsideComplexGridColumn(id, formModel);
};

export const validateControls = ({ formKey, reduxState }) => {
  const controls = getControls();
  const warnings = [];
  const controlsInsideContainers = [];
  const requiredSubGridsSet = new Set();

  const translations = reduxState.translations;
  const formIsAutoChecking = reduxState.workflow?.formIsAutoChecking;
  const { formModel, entityDefinition } = reduxState.formDesigner[formKey];
  const ssContext = getSsContext(reduxState, { formKey });
  const entityType = Object.keys(entityDefinition)[0];

  Object.entries(controls).forEach(([instanceId, props]) => {
    const { isHidden, isRequired, label, containersPropsJson, rowId } = props;
    let { value } = props;
    const [id, indexStr] = instanceId.split('#');
    const { controlType, entityName, fieldName, lookupType, maxValue, minValue } = formModel[id];

    if (shouldSkipValidation(isHidden, id, formModel)) {
      return;
    }

    if (controlType === SS_CONTROL_TYPE.TEXT_AREA) {
      value = getHtmlFreeValue(value).trim();
    }

    switch (controlType) {
      case SS_CONTROL_TYPE.NUMERIC_TEXT_BOX:
      case SS_CONTROL_TYPE.SLIDER: {
        const dataType = getDataType(entityDefinition, entityType, entityName, fieldName);

        validateSsNumericTextBox({ dataType, value, label, instanceId, warnings, translations });

        if (controlType === SS_CONTROL_TYPE.SLIDER) {
          validateSsSlider({
            value,
            label,
            instanceId,
            minValue,
            maxValue,
            warnings,
            translations,
            ssContext: {
              ...ssContext,
              contextDataMember: entityName,
              gridArrIndex: JSON.parse(indexStr),
            },
          });
        }

        break;
      }

      case SS_CONTROL_TYPE.DATE_BOX: {
        validateSsDateBox({ value, label, instanceId, warnings, translations });
        break;
      }

      case SS_CONTROL_TYPE.TEXT_BOX: {
        if (lookupType === LOOKUP_TYPES.AUTOCOMPLETE && value) {
          validateSsTextBox({
            rowId,
            instanceId,
            value,
            label,
            translations,
            warnings,
          });
        }
        break;
      }

      default:
        break;
    }

    if (!isRequired || !isNilOrEmpty(value)) {
      return;
    }

    if (isMobileDevice()) {
      // we don't want to create grouped warnings on mobile devices
      const warning = {
        instanceId,
        source: WARNING_SOURCES.VALIDATION,
        message: `${label} ${translate(translations, 'is required')}.`,
        warningEnabled: formIsAutoChecking,
        rowId,
      };

      setContainersInfoForWarning(warning);

      warnings.push(warning);
      return;
    }

    if (!containersPropsJson) {
      // control is outside of containers
      warnings.push({
        instanceId,
        source: WARNING_SOURCES.VALIDATION,
        message: `${label} ${translate(translations, 'is required')}.`,
        warningEnabled: formIsAutoChecking,
        rowId,
      });

      return;
    }

    const containersProps = JSON.parse(containersPropsJson);

    for (const props of containersProps) {
      if (props.subGridInstanceId) {
        requiredSubGridsSet.add(props.subGridInstanceId);
      }
    }

    const allContainersActive = containersProps.every(({ isActive }) => isActive);

    if (allContainersActive) {
      warnings.push({
        instanceId,
        source: WARNING_SOURCES.VALIDATION,
        message: `${label} ${translate(translations, 'is required')}.`,
        warningEnabled: formIsAutoChecking,
      });

      return;
    }

    for (const props of containersProps) {
      if (props.isActive) {
        continue;
      }

      controlsInsideContainers.push({
        containerId: props.id,
        containerLabel: props.label,
      });

      return;
    }
  });

  // Validate containers with not rendered elements, but we want just one warning per container.
  const uniqueContainers = controlsInsideContainers.reduce(
    (acc, control) => ({ ...acc, [control.containerId]: control.containerLabel }),
    {}
  );

  Object.keys(uniqueContainers).forEach((containerId) => {
    const { controlType } = formModel[containerId] ?? {};
    let type = '';
    if (controlType === SS_CONTROL_TYPE.SINGLE_TAB) {
      type = 'tab';
    }
    const message = type
      ? `${translate(translations, 'Please complete tab')} '${uniqueContainers[containerId]}'`
      : formatString(
          translate(translations, "Please complete '{0}' Section"),
          uniqueContainers[containerId]
        );

    warnings.unshift({
      instanceId: containerId,
      source: WARNING_SOURCES.VALIDATION,
      message,
      warningEnabled: formIsAutoChecking,
      containerInfo: {
        controlType,
        containerId,
        requiredSubGrids: Array.from(requiredSubGridsSet),
        formModel,
      },
    });
  });

  return warnings;
};

export const getValueByDataSourceTextField = (
  values,
  dataValueStr,
  dataTextField,
  dataValueField
) => {
  if (!Array.isArray(values)) {
    return dataValueStr;
  }
  const value = values.find((value) => value?.[dataValueField] === dataValueStr);
  const translatedValue = value?.LNG_TRANSLATION || value?.[dataTextField] || dataValueStr;

  return translatedValue;
};

const getTableRowsWithoutValues = (rows, isParentless) => {
  const keysToPick = ['key', isParentless ? 'gridArrIndex' : 'index', 'isFilteredOut'];

  return rows.map((row) => _.pick(row, keysToPick));
};

export const getTableRowsEqualityFn = (isParentless = false, invalidate = false) => (
  prevRows,
  nextRows
) => {
  if (prevRows.length !== nextRows.length || invalidate) {
    return false;
  }

  const prevRowsWithoutValues = getTableRowsWithoutValues(prevRows, isParentless);
  const nextRowsWithoutValues = getTableRowsWithoutValues(nextRows, isParentless);

  return _.isEqual(prevRowsWithoutValues, nextRowsWithoutValues);
};

const _genId = (index) => `NR_${index}`;

export const generateRowId = (rows, primaryKey = 'PK_ID') => {
  if (_.isNil(rows)) {
    return _genId(1);
  }

  if (!Array.isArray(rows) && !_.isPlainObject(rows)) {
    throw new Error('Invalid rows value');
  }

  const firstElement = rows[0];

  if (_.isNil(firstElement)) {
    return _genId(1);
  }

  const id = firstElement?.[primaryKey];

  if (_.isNil(id)) {
    return _genId(1);
  }

  const indexOfSeparator = id.indexOf('_');

  if (indexOfSeparator < 0) {
    return _genId(1);
  }

  const latestIndex = Number(id.substr(indexOfSeparator + 1));

  return _genId(latestIndex + 1);
};

export const getGridArrIndexForRowFilter = (gridArrIndex, gridIdx) => {
  return Array.isArray(gridArrIndex) && gridArrIndex.length >= 2
    ? gridArrIndex.slice(0, gridArrIndex.length - 1).concat([gridIdx])
    : gridIdx;
};

const evaluateColumnSSValue = (column, ssContext) => {
  const { data, gridArrIndex, entityDefinition } = ssContext;
  const [entityName, fieldName] = column.split('.');

  const srcPath = getDataPath(entityName, gridArrIndex, entityDefinition);
  const fieldObject = _.get(data?.entityDataStore?.entityData, srcPath, {});

  return fieldObject[fieldName];
};

const evaluateSSValue = (type, value, ssContext) => {
  switch (type) {
    case 'column':
      return evaluateColumnSSValue(value, ssContext);
    case 'formula':
      return evalSSEexpression(value, ssContext);
    default:
      return value;
  }
};

export const transformFiltersForAPI = (filters, ssContext) => {
  return filters.map((f) => ({
    openGroup: String(f.OpenGroup),
    fieldName: f.LeftValue,
    sqlOperator: f.Operator ?? f.SqlOperator,
    value: getFilterValue(f.RightType, f.RightValue, ssContext),
    relationalOperator: f.RelationalOperator,
    closeGroup: String(f.CloseGroup),
  }));
};

export const getDynamicParamCollection = (paramList, ssContext) => {
  return paramList.reduce(
    (collection, param) => ({
      ...collection,
      [param.Name]: evaluateSSValue(param.Type, param.Value, ssContext) ?? null,
    }),
    {}
  );
};

export const getValueWithDataType = (
  value,
  entityDefinition,
  entityType,
  dataMember,
  dataField
) => {
  const dataType = getDataType(entityDefinition, entityType, dataMember, dataField);
  const formatType = getFormatType(entityDefinition, entityType, dataMember, dataField);
  const defaultValue = getDefaultValueFromEntityDefinition(
    entityDefinition,
    entityType,
    dataMember,
    dataField
  );
  const valueWithType = value2valueWithType(value, dataType, formatType, defaultValue);

  return valueWithType;
};

export const translateRows = ({ rows, columns = [], translateString }) => {
  const columnsForTranslation = columns.filter(
    (column) =>
      column.childControlType === SS_CONTROL_TYPE.TEXT_VIEW && column.dataType === SSString
  );

  if (!columnsForTranslation.length) {
    return rows;
  }

  return rows.map((row) => {
    const newRow = { ...row };
    columnsForTranslation.forEach((column) => {
      const value = newRow[column.accessor];
      newRow[column.accessor] = translateString(value) ?? value;
    });

    return newRow;
  });
};

const getBladeGroupWidthDivisorArr = (selectedSubGridsCount) =>
  selectedSubGridsCount === 3 ? [3.333, 3.333, 2.5] : [2, 2];

export const getBladeGroupWidthArrWorkflowStepPage = (formHasErrors, selectedSubGridsCount) => {
  const windowWidth = window.innerWidth;
  const stepFormContainerWidth = windowWidth - SIDEBAR_WIDTH;
  const divisorArr = getBladeGroupWidthDivisorArr(selectedSubGridsCount);

  return Array(selectedSubGridsCount)
    .fill()
    .map((_, i) =>
      formHasErrors
        ? (stepFormContainerWidth - ERROR_CONTAINER_FLEX_BASIS) / divisorArr[i]
        : stepFormContainerWidth / divisorArr[i]
    );
};

export const getBladeGroupWidthArrEntityDisplayPage = (selectedSubGridsCount, panelConfig) => {
  const windowWidth = window.innerWidth;
  const fullWidth = windowWidth - SIDEBAR_WIDTH;
  const panelLayoutContainerWidth = fullWidth - ENTITY_DISPLAY_LEFT_RIGHT_PADDING * 2;
  const filledArray = Array(selectedSubGridsCount).fill();

  const isLeftPanelClosed = [
    PANEL_CONFIGURATIONS.ONLY_MIDDLE,
    PANEL_CONFIGURATIONS.ONLY_RIGHT,
    PANEL_CONFIGURATIONS.LEFT_COLLAPSED,
  ].includes(panelConfig);

  if (isLeftPanelClosed) {
    const divisorArr = getBladeGroupWidthDivisorArr(selectedSubGridsCount);
    return filledArray.map((_, i) => fullWidth / divisorArr[i]);
  }

  if (selectedSubGridsCount === 3) {
    const leftPanelWidth = panelLayoutContainerWidth * 0.2 + ENTITY_DISPLAY_LEFT_RIGHT_PADDING;
    const divisorArr = getBladeGroupWidthDivisorArr(selectedSubGridsCount);
    return filledArray.map((_, i) => (fullWidth - leftPanelWidth) / divisorArr[i]);
  }

  const addedWidth =
    selectedSubGridsCount === 2
      ? ENTITY_DISPLAY_LEFT_RIGHT_PADDING / 2
      : ENTITY_DISPLAY_LEFT_RIGHT_PADDING;

  return filledArray.map(() => panelLayoutContainerWidth / 2.5 + addedWidth);
};

export const getContainerWidthPercentWorkflowStepPage = (selectedSubGridsCount) =>
  selectedSubGridsCount ? 50 : 100;

export const getContainerWidthPercentEntityDisplayPage = ({
  formKey,
  defaultWidthPercent,
  panelConfig,
  subGridsCount,
  stepFormHasSubGrids,
  detailFormHasSubGrids,
}) => {
  if (
    formKey === FORM_KEYS.STEP &&
    panelConfig === PANEL_CONFIGURATIONS.LEFT_COLLAPSED &&
    detailFormHasSubGrids
  ) {
    return 50;
  }

  if (formKey === FORM_KEYS.DETAIL && stepFormHasSubGrids) {
    if (panelConfig === PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED) {
      return 40;
    }

    if (panelConfig === PANEL_CONFIGURATIONS.ONLY_RIGHT) {
      return 50;
    }
  }

  if (!subGridsCount) {
    return defaultWidthPercent;
  }

  if (formKey === FORM_KEYS.STEP) {
    switch (panelConfig) {
      case PANEL_CONFIGURATIONS.LEFT_COLLAPSED:
      case PANEL_CONFIGURATIONS.ONLY_MIDDLE:
        return 50;
      case PANEL_CONFIGURATIONS.RIGHT_COLLAPSED:
        return 40;
      default:
        return defaultWidthPercent;
    }
  }

  if (formKey === FORM_KEYS.DETAIL) {
    switch (panelConfig) {
      case PANEL_CONFIGURATIONS.MIDDLE_COLLAPSED:
        return 40;
      case PANEL_CONFIGURATIONS.ONLY_RIGHT:
        return 50;
      default:
        return defaultWidthPercent;
    }
  }
};

export const getIsDynamicInputDisabled = (ssModalMappingValues, entityName, fieldName) => {
  if (!ssModalMappingValues) {
    return false;
  }

  return Object.keys(ssModalMappingValues).includes(`${entityName}.${fieldName}`);
};

export const checkDevGridError = (rowKey, entityName) => {
  if (
    !rowKey &&
    process.env.NODE_ENV === 'development' &&
    entityName !== 'V_ENU_NONPERSIS_USER_DATA'
  ) {
    throw new Error('rowKey is required but missing in "getGridRows" selector');
  }
};

export const filterRowsByMappingValues = (rows, mappingValues) =>
  rows.filter((row) =>
    Object.entries(mappingValues).every(([updateTo, value]) => {
      const fieldName = updateTo.split('.')[1];
      return _.isEqual(row[fieldName], value);
    })
  );

export const filterChildAttachmentRows = (rows, parentEntityName, parentRowId) =>
  rows.filter(
    (row) =>
      row[BAH_CHILD_DATAMEMBER] === parentEntityName && row[BAH_CHILD_RECORD_ID] === parentRowId
  );

export const getOrderedRules = (rules = []) => {
  const setDefaultRules = rules.filter((rule) =>
    rule.actions?.some(({ type }) => type === ACTION_TYPE_SET_DEFAULT)
  );
  const otherRules = rules.filter(
    (rule) => !rule.actions?.some(({ type }) => type === ACTION_TYPE_SET_DEFAULT)
  );

  return [...setDefaultRules, ...otherRules];
};

export const getIsEditMode = ({ editAllRows, editedRows, row }) =>
  editAllRows ? true : editedRows[row.key]?.editMode;

export const getFormsWatchValues = (ssContext, primaryKeysMap) => {
  const { formModel, entityDefinition, data } = ssContext;
  const formsWatchValues = {};

  const formsIds = Object.values(formModel).reduce(
    (acc, control) =>
      control.controlType === SS_CONTROL_TYPE.FORM ? [...acc, control.controlId] : acc,
    []
  );

  formsIds.forEach((id) => {
    if (!formModel[id].update) {
      formsWatchValues[id] = '[]';
      return;
    }

    const watchValues = [];
    const entityData = data.entityDataStore?.entityData ?? {};
    const entityType = Object.keys(entityDefinition)[0];
    const dataMembers = Object.keys(primaryKeysMap);
    const dataMembersIndexesMap = getDataMembersIndexesMap(dataMembers, entityData, entityType);
    const watchValuesObjectsArr = getWatchValuesObjectArr(
      formModel[id].update.rules,
      dataMembersIndexesMap
    );

    watchValuesObjectsArr.forEach((item) => {
      switch (item.type) {
        case 'column':
          {
            const { entityName, fieldName, index } = item;
            const dataPath = getDataPath(entityName, index, entityDefinition);
            watchValues.push(_.get(entityData, [...dataPath, fieldName]));
          }
          break;

        case 'formula':
          {
            const { value, index, contextDataMember } = item;
            watchValues.push(
              evalSSEexpression(value, { ...ssContext, gridArrIndex: index, contextDataMember })
            );
          }
          break;

        default:
          break;
      }
    });

    formsWatchValues[id] = JSON.stringify(watchValues);
  });

  return formsWatchValues;
};

export const getFormChangesBladeDefaultTab = (deltaChanges, layoutChanges) => {
  if (deltaChanges.controls?.length) {
    return 0;
  }
  if (layoutChanges.AddedFields?.length) {
    return 1;
  }
  if (layoutChanges.DeletedFields?.length) {
    return 2;
  }
  return 3;
};

export const isValidClusterColumnChartData = (rawChartData) => {
  if (!rawChartData?.length) {
    return false;
  }

  return rawChartData.every((chartItem) => {
    if (!chartItem.clusterData?.length || !chartItem.label?.length) {
      return false;
    }

    return chartItem.clusterData.every(
      (clusterItem) => clusterItem.label?.length && clusterItem.value?.length
    );
  });
};

export const filterEntityData = (
  entityData,
  fields,
  entityDefinition,
  translations,
  store,
  formKey
) => {
  const entityType = Object.keys(entityDefinition)[0];
  const entityTranslations = translations.Entity || {};
  const translatedEntityType = entityTranslations[entityType] || entityType;

  const result = {
    [translatedEntityType]: {},
  };

  if (isNilOrEmpty(entityData) || isNilOrEmpty(fields)) {
    return result;
  }

  const uniqueEntityNames = [...new Set(fields.map(({ field }) => field.split('.')[0]))];
  const entityNamesIndexesMap = getDataMembersIndexesMap(uniqueEntityNames, entityData, entityType);

  fields.forEach(({ field, label = '' }) => {
    const [entityName, fieldName] = field.split('.');
    const indexes = entityNamesIndexesMap[entityName];

    indexes.forEach((index) => {
      const fieldValuePath = [...getDataPath(entityName, index, entityDefinition), fieldName];
      const pathStr = fieldValuePath.join('.');
      const value = _.get(entityData, pathStr);

      if (isNilOrEmpty(value)) {
        return;
      }

      let translatedFieldName = label;
      const expression = label.split('eval||')[1];

      if (label.startsWith('eval||') && expression) {
        const ssContext = getSsContext(store, { index, formKey, entityName });
        const evaluatedExpression = evalSSEexpression(expression, ssContext);

        translatedFieldName =
          evaluatedExpression === '_eval_error_' ? expression : evaluatedExpression;
      } else if (!label) {
        translatedFieldName = getPropTranslation(entityDefinition, translations, field);
      }

      const translatedEntityPath = getDataPath(entityName, index, entityDefinition).map(
        (pathItem) => {
          const entityName = typeof pathItem === 'string' ? pathItem.split('-').pop() : pathItem;
          return entityTranslations[entityName] || entityName;
        }
      );

      const fullTranslatedPath = [...translatedEntityPath, translatedFieldName].join('.');

      _.set(result, fullTranslatedPath, value);
    });
  });

  return result;
};

export const createExtraPrompt = ({ extraPrompts = [], formKey, entityName, index = [], store }) =>
  extraPrompts.reduce((acc, { promptMessage = '' }) => {
    const expression = promptMessage.split('eval||')[1];

    if (!promptMessage || !expression) {
      return acc;
    }

    let prompt = promptMessage;

    if (promptMessage.startsWith('eval||')) {
      //if user selects some nested field (e.g. 2nd, 3rd level) but parent control is at higher level
      //we need to provide extra index depth to take at least first value from deepest levels
      const extraIndexDepth = [0, 0];

      const ssContext = getSsContext(store, {
        formKey,
        entityName,
        index: [...index, ...extraIndexDepth],
      });
      const evaluatedExpression = evalSSEexpression(expression, ssContext);

      prompt = evaluatedExpression === '_eval_error_' ? expression : evaluatedExpression;
    }

    return prompt && acc.length ? `${acc}\n${prompt}` : prompt || acc;
  }, '');
